import actions from "src/actions";
import type { FacebookPage, InstagramAccount } from "../types";
import {
  mapPartnershipPartnerOptionsResponse,
  type PartnershipIdentityCatalog,
  type PartnershipPartnerOptionsApiResponse,
} from "./mapPartnershipPartnerOptionsResponse";

type ListPagesApiRow = {
  id: string;
  name: string;
  username?: string;
  picture?: string;
};

/** GET /ads/pages — full managed + promote_pages union for advertiser identity pickers. */
export function mapAdvertiserIdentityPagesFromListPages(
  rows: readonly ListPagesApiRow[] | null | undefined
): FacebookPage[] {
  return (rows ?? []).map((page) => ({
    id: page.id,
    name: page.name,
    username: page.username,
    picture: page.picture ?? "",
    is_ad_account_page: true,
  }));
}

export async function fetchAdvertiserIdentityPages(args: {
  ad_account_id: string;
}): Promise<FacebookPage[]> {
  const rows = (await actions.ADMIN.listPages({
    ad_account_id: args.ad_account_id.trim(),
  })) as ListPagesApiRow[];
  return mapAdvertiserIdentityPagesFromListPages(rows);
}

/** GET /ads/instagram_accounts — full ad-account IG catalog for advertiser identity pickers. */
export async function fetchAdvertiserIdentityInstagramAccounts(args: {
  ad_account_id: string;
}): Promise<InstagramAccount[]> {
  const rows = (await actions.ADMIN.listInstagramAccounts({
    ad_account_id: args.ad_account_id.trim(),
  })) as InstagramAccount[];
  return rows ?? [];
}

/** Advertiser Facebook pages + Instagram catalog for identity pickers. */
export async function fetchAdvertiserIdentityData(args: {
  ad_account_id: string;
}): Promise<{
  pages: FacebookPage[];
  instagramAccounts: InstagramAccount[];
}> {
  const [pages, instagramAccounts] = await Promise.all([
    fetchAdvertiserIdentityPages(args),
    fetchAdvertiserIdentityInstagramAccounts(args),
  ]);
  return { pages, instagramAccounts };
}

export type PartnershipPartnerOptionsLoad = PartnershipIdentityCatalog;

/**
 * Full partnership_partner_options fetch. Used for the initial advertiser catalog load
 * and for permission-aware refetches while partner post is enabled — not on every
 * advertiser field edit when partner post is off.
 */

/** Splits one API catalog into the state fields screens pass to creative editors. */
export function partnershipIdentityCatalogToEditorState(
  catalog: PartnershipIdentityCatalog
): {
  advertiserPages: PartnershipIdentityCatalog["advertiserFacebookPages"];
  partnerPages: PartnershipIdentityCatalog["partnerFacebookPages"];
  instagramAccounts: PartnershipIdentityCatalog["instagramAccounts"];
  partnerInstagramAccounts: PartnershipIdentityCatalog["partnerInstagramAccounts"];
  advertiserValidationMessages: string[];
  partnershipPermissionsLoaded: boolean;
  metaBusinessId?: string;
} {
  return {
    advertiserPages: catalog.advertiserFacebookPages,
    partnerPages: catalog.partnerFacebookPages,
    instagramAccounts: catalog.instagramAccounts,
    partnerInstagramAccounts: catalog.partnerInstagramAccounts,
    advertiserValidationMessages: catalog.advertiserValidationMessages,
    partnershipPermissionsLoaded: catalog.partnershipPermissionsLoaded,
    metaBusinessId: catalog.metaBusinessId,
  };
}

export function partnershipCatalogEditorSliceForKey(
  catalogByKey: Readonly<Record<string, PartnershipIdentityCatalog>>,
  fetchKey: string
): ReturnType<typeof partnershipIdentityCatalogToEditorState> | undefined {
  const catalog = catalogByKey[fetchKey];
  if (!catalog) {
    return undefined;
  }
  return partnershipIdentityCatalogToEditorState(catalog);
}

export async function fetchPartnershipPartnerOptions(args: {
  ad_account_id: string;
  advertiser_page_id?: string;
  advertiser_instagram_user_id?: string;
  eligible_only?: boolean;
  instagram_accounts_only?: boolean;
}): Promise<PartnershipIdentityCatalog> {
  const data = await actions.ADMIN.listPartnershipPartnerOptions({
    ad_account_id: args.ad_account_id.trim(),
    advertiser_page_id: args.advertiser_page_id?.trim() || undefined,
    advertiser_instagram_user_id:
      args.advertiser_instagram_user_id?.trim() || undefined,
    eligible_only: args.eligible_only,
    instagram_accounts_only: args.instagram_accounts_only,
  });
  return mapPartnershipPartnerOptionsResponse(
    data as PartnershipPartnerOptionsApiResponse
  );
}
